import React, { useCallback, useState } from 'react';
import { TextField as Field, BaseTextFieldProps, IconButton, InputAdornment } from '@mui/material';
import { ReactComponent as EyeOpen } from 'assets/icons/EyeOpen.svg';
import { ReactComponent as EyeClosed } from 'assets/icons/EyeClosed.svg';
import { useStyles } from './styles';
import ControlledError from '../ControlledError';

export interface ITextFieldProps extends BaseTextFieldProps {
  type?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  value?: any;
  error?: any;
  required?: boolean;
  skippable?: boolean;
  variant?: 'outlined' | 'standard' | 'filled';
  fullWidth?: boolean;
  multiline?: boolean;
  rows?: number;
  autoComplete?: string;
  autoFocus?: boolean;
  inputProps?: any;
  InputProps?: any;
  className?: any;
  disabled?: boolean;
  readOnly?: boolean;
  style?: any;
  inputRef?: any;
  isSearchBar?: boolean;
  maskText?: boolean;
  testId?: string;
  onChange?: (e: any) => void;
  onBlur?: (e: any) => void;
  onFocus?: (e: any) => void;
  onMouseDown?: (e: any) => void;
  onTouchEnd?: (e: any) => void;
  onClick?: (e: any) => void;
}

const TextField = ({
  type = 'text',
  name,
  label,
  placeholder,
  value,
  error,
  required = false,
  skippable = false,
  variant = 'outlined',
  fullWidth = false,
  multiline = false,
  rows = 1,
  autoComplete = 'off',
  autoFocus = false,
  inputProps,
  InputProps,
  className,
  disabled,
  readOnly,
  style,
  inputRef,
  isSearchBar,
  maskText = false,
  testId,
  onChange,
  onBlur,
  onFocus,
  ...params
}: ITextFieldProps) => {
  const classes = useStyles({ isSearchBar: isSearchBar });
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const hasPasswordToggle = type === 'password' || maskText;
  const passwordAdornment = hasPasswordToggle
    ? {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={handleTogglePassword}
              data-testid={testId ? `${testId}-visibility-toggle` : undefined}
              edge="end"
              size="large"
            >
              {showPassword ? <EyeClosed /> : <EyeOpen />}
            </IconButton>
          </InputAdornment>
        ),
      }
    : {};

  const mergedInputProps = {
    ...InputProps,
    ...passwordAdornment,
  };

  return (
    <ControlledError show={!!error} error={!!error && error}>
      <Field
        label={
          label && (
            <>
              <span>{label}</span>
              {(required || skippable) && (
                <span className={required ? classes.requiredMark : classes.skippableMark}>{required ? '*' : '**'}</span>
              )}
            </>
          )
        }
        variant={variant ? variant : 'outlined'}
        data-testid={testId}
        placeholder={placeholder}
        type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
        sx={maskText && !showPassword ? { '& input': { WebkitTextSecurity: 'disc' } } : undefined}
        autoComplete={autoComplete}
        fullWidth={fullWidth}
        name={name}
        value={value}
        multiline={multiline ? true : false}
        rows={rows}
        error={!!error}
        inputProps={{
          ...inputProps,
          readOnly,
        }}
        InputProps={mergedInputProps}
        className={`${classes.input} ${multiline ? classes.multilineInput : ''} ${className ? className : null}`}
        disabled={disabled}
        style={style}
        inputRef={inputRef}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        {...params}
      />
    </ControlledError>
  );
};

export default React.memo(TextField);
